import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  Learnpath,
  LearnpathActivity,
  LearnpathActivityType,
  ActivityContent,
  LearningPathStudentGeneral,
  LearningPathOverview,
  Student,
  LearningPathStudentSubscription,
  LearningPathStudentDashboard,
  LearningPathStudentDetail,
  LearningPathStudentActivityGeneral,
  LearningPathStudent,
  StudentActivityWithHelpers,
  LearningPathStudentActivityStatus,
  LearnpathActivityDetailed,
} from '../models/interfaces';
import { EndPointService } from 'src/app/core/services/end-point.service';
import { StudentOverview } from '../models/StudentOverview';
import { LearningPathStudentActivityAttachment } from '../models/LearningPathStudentActivityAttachment';
import { LearningPathStudentActivityElearning } from '../models/LearningPathStudentActivityElearning';

import { StudentActivityStudent } from '../models/StudentActivityStudent';
import { Course } from '../models/Course';
import { PagingState } from 'src/app/shared/framework/paging/PagingState';
import { LearningPathStudentActivityStatusLog } from '../models/LearningPathStudentActivityStatusLog';
import { AuthService } from 'src/app/auth/services/auth.service';
import { DelegatedTeam } from 'src/app/auth/models/UserData';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class LearnpathService {
  private _apiLearnPathsBase = '/api/lms/learningpaths/';

  private _LearnPaths = 'overview';
  private _LearnPathUpdate = 'update';
  private _LearnPathById = 'getbyid';
  private _LearnPathDelete = 'remove';

  private _LearnPathActivities = 'activity';
  private _LearnPathActivityAdd = 'activity/add';
  private _LearnPathActivitiesUpdate = 'activity/updatelist';
  private _LearnPathActivityUpdate = 'activity/update';
  private _LearnPathActivityDelete = 'activity/delete';
  private _ActivityTypes = 'activity/types';
  private _ActivityContent = 'activity/contentlist';
  private _ActivityContentCount = 'activity/contentlistcount';
  private _ActivityStatusses = 'activity/statusses';
  private _ActivityChangeStatus = 'activity/changestatus';
  private _ActivityAttachments = 'activity/attachment/getall';
  private _ActivityStudents = 'activity/students';
  private _activityContentScores = 'activity/content/scores';
  private _activityContentDescription = 'activity/content/description';
  private _managerComment = 'activity/managercomment';
  private _activityStatusHistory = 'activity/statushistory';
  private _studentActivities = 'student/activities';
  private _studentLongestNotStarted = 'student/longestnotstarted';

  private _LearnPathUnsubscribed = 'student/getunsubscribed';
  private _LearnPathSubscribed = 'student/getsubscribed';
  private _LearnPathInvite = 'student/invite';
  private _LearnPathSubscribe = 'student/subscribe';
  private _LearnPathSubscribeEmp = 'student/subscribeemp';
  private _LearnPathComplete = 'student/complete';
  private _LearnPathResume = 'student/resume';
  private _LearnPathRestart = 'student/restart';
  private _LearnPathUnsubscribe = 'student/unsubscribe';
  private _LearnPathStudentOverview = 'student/overview';
  private _LearnPathStudentDashboard = 'student/dashboard';
  private _learnpathReminder = 'student/remind';
  private _LearnPathStudentDetail = 'student/detail';
  private _LearnPathEssActivities = 'ess/activities';
  private _LearnPathEssLearnPath = 'ess/learnpath';
  private _LearnPathEssLearnPathAccept = 'ess/learnpath/accept';
  private _LearnPathEssLearnPaths = 'ess/learnpaths';
  private _LearnPathActivitiesWithHelp = 'ess/studentactivitiesfull';
  private _LearnPathActivityAttachmentDelete = 'ess/activity/attachment/delete';
  private _LearnPathActivityCheck = 'ess/activity/check';
  private _LearnPathActivityStart = 'ess/activity/start';
  private _LearnPathActivityContentUrl = 'ess/activity/getcontenturl';
  private _LearnPathActivitySaveComment = 'ess/activity/comment';
  private _LearnPathActivitySaveAdmin = 'ess/activity/saveadmin';
  private _LearnPathActivityComplete = 'ess/activity/complete';

  private _canDeleteExistingActivity = 'activity/candeleteexistingactivity';
  private _deleteExistingActivity = 'activity/deleteexistingactivity';

  constructor(
    private _endPointService: EndPointService,
    private _authService: AuthService,
    private http: HttpClient
  ) {
    this.registerEndPoints();
  }

  //#region register endpoints
  private registerEndPoints(): void {
    this._endPointService.registerEndPoint<LearningPathOverview[]>(
      'learnpath_overview',
      `${this._apiLearnPathsBase}${this._LearnPaths}`
    );
    this._endPointService.registerEndPoint<Learnpath>(
      'learnpath_get',
      `${this._apiLearnPathsBase}${this._LearnPathById}`
    );
    this._endPointService.registerEndPoint<void>(
      'learnpath_delete',
      `${this._apiLearnPathsBase}${this._LearnPathDelete}`,
      false
    );
    this._endPointService.registerEndPoint<ActivityContent[]>(
      'activity_content_get',
      `${this._apiLearnPathsBase}${this._ActivityContent}`,
      true
    );
    this._endPointService.registerEndPoint<number>(
      'activity_content_count',
      `${this._apiLearnPathsBase}${this._ActivityContentCount}`,
      true
    );
    this._endPointService.registerEndPoint<LearnpathActivity[]>(
      'activities_get',
      `${this._apiLearnPathsBase}${this._LearnPathActivities}`,
      true
    );
    this._endPointService.registerEndPoint<LearnpathActivity>(
      'activity_add',
      `${this._apiLearnPathsBase}${this._LearnPathActivityAdd}`,
      false,
      ['activities_get']
    );
    this._endPointService.registerEndPoint<LearnpathActivity[]>(
      'activities_save',
      `${this._apiLearnPathsBase}${this._LearnPathActivitiesUpdate}`,
      false
    );
    this._endPointService.registerEndPoint<LearnpathActivity>(
      'activity_save',
      `${this._apiLearnPathsBase}${this._LearnPathActivityUpdate}`,
      false
    );
    this._endPointService.registerEndPoint<void>(
      'activity_delete',
      `${this._apiLearnPathsBase}${this._LearnPathActivityDelete}`,
      false
    );
    this._endPointService.registerEndPoint<LearnpathActivityType[]>(
      'activities_type_get',
      `${this._apiLearnPathsBase}${this._ActivityTypes}`,
      true
    );
    this._endPointService.registerEndPoint<
      LearningPathStudentActivityGeneral[]
    >(
      'student_activities',
      `${this._apiLearnPathsBase}${this._studentActivities}`,
      true
    );
    this._endPointService.registerEndPoint<Learnpath>(
      'learnpath_save',
      `${this._apiLearnPathsBase}${this._LearnPathUpdate}`,
      false
    );
    this._endPointService.registerEndPoint<Student[]>(
      'unsubscribed_get',
      `${this._apiLearnPathsBase}${this._LearnPathUnsubscribed}`,
      true
    );
    this._endPointService.registerEndPoint<LearningPathStudentGeneral[]>(
      'subscribed_get',
      `${this._apiLearnPathsBase}${this._LearnPathSubscribed}`,
      true
    );
    this._endPointService.registerEndPoint<StudentOverview>(
      'studentoverview_get',
      `${this._apiLearnPathsBase}${this._LearnPathStudentOverview}`,
      true
    );
    this._endPointService.registerEndPoint<LearningPathStudentDashboard>(
      'studentdashboard_get',
      `${this._apiLearnPathsBase}${this._LearnPathStudentDashboard}`,
      true
    );
    this._endPointService.registerEndPoint<LearningPathStudentDetail>(
      'studentdetail_get',
      `${this._apiLearnPathsBase}${this._LearnPathStudentDetail}`,
      true
    );
    this._endPointService.registerEndPoint<LearningPathStudentGeneral>(
      'employee_invite',
      `${this._apiLearnPathsBase}${this._LearnPathInvite}`,
      false
    );
    this._endPointService.registerEndPoint<LearningPathStudentGeneral>(
      'employee_subscribe',
      `${this._apiLearnPathsBase}${this._LearnPathSubscribe}`,
      false,
      [
        'studentoverview_get',
        'subscribed_get',
        'unsubscribed_get',
        'learnpath_overview',
        'studentdashboard_get',
        'activity_students_get',
      ]
    );
    this._endPointService.registerEndPoint<LearningPathStudentGeneral>(
      'employee_subscribeemp',
      `${this._apiLearnPathsBase}${this._LearnPathSubscribeEmp}`,
      false
    );
    this._endPointService.registerEndPoint<void>(
      'employee_complete',
      `${this._apiLearnPathsBase}${this._LearnPathComplete}`,
      false
    );
    this._endPointService.registerEndPoint<void>(
      'employee_resume',
      `${this._apiLearnPathsBase}${this._LearnPathResume}`,
      false,
      ['subscribed_get', 'unsubscribed_get']
    );
    this._endPointService.registerEndPoint<void>(
      'employee_restart',
      `${this._apiLearnPathsBase}${this._LearnPathRestart}`,
      false,
      ['subscribed_get', 'unsubscribed_get']
    );
    this._endPointService.registerEndPoint<void>(
      'employee_unsubscribe',
      `${this._apiLearnPathsBase}${this._LearnPathUnsubscribe}`,
      false,
      ['subscribed_get', 'unsubscribed_get']
    );
    this._endPointService.registerEndPoint<LearningPathStudent>(
      'student_longest_notstarted_get',
      `${this._apiLearnPathsBase}${this._studentLongestNotStarted}`,
      true
    );
    this._endPointService.registerEndPoint<StudentActivityWithHelpers>(
      'activities_with_help_get',
      `${this._apiLearnPathsBase}${this._LearnPathActivitiesWithHelp}`,
      true
    );
    this._endPointService.registerEndPoint<
      LearningPathStudentActivityGeneral[]
    >(
      'myactivities_get',
      `${this._apiLearnPathsBase}${this._LearnPathEssActivities}`
    );
    this._endPointService.registerEndPoint<LearningPathStudent[]>(
      'mylearnpaths_get',
      `${this._apiLearnPathsBase}${this._LearnPathEssLearnPaths}`
    );
    this._endPointService.registerEndPoint<LearningPathStudent>(
      'mylearnpath_get',
      `${this._apiLearnPathsBase}${this._LearnPathEssLearnPath}`
    );
    this._endPointService.registerEndPoint(
      'mylearnpath_accept',
      `${this._apiLearnPathsBase}${this._LearnPathEssLearnPathAccept}`
    );
    this._endPointService.registerEndPoint<LearningPathStudentActivityStatus[]>(
      'activityStatusses_get',
      `${this._apiLearnPathsBase}${this._ActivityStatusses}`
    );
    this._endPointService.registerEndPoint<LearningPathStudentActivityGeneral>(
      'activityStatus_change',
      `${this._apiLearnPathsBase}${this._ActivityChangeStatus}`,
      false
    );
    this._endPointService.registerEndPoint<
      LearningPathStudentActivityStatusLog[]
    >(
      'activity_status_history',
      `${this._apiLearnPathsBase}${this._activityStatusHistory}`,
      true
    );
    this._endPointService.registerEndPoint<void>(
      'activity_add_manager_comment',
      `${this._apiLearnPathsBase}${this._managerComment}`,
      false,
      ['studentdetail_get']
    );
    this._endPointService.registerEndPoint(
      'activity_delete_attachment',
      `${this._apiLearnPathsBase}${this._LearnPathActivityAttachmentDelete}`,
      false
    );
    this._endPointService.registerEndPoint<boolean>(
      'activity_check',
      `${this._apiLearnPathsBase}${this._LearnPathActivityCheck}`,
      false
    );
    this._endPointService.registerEndPoint(
      'activity_start',
      `${this._apiLearnPathsBase}${this._LearnPathActivityStart}`,
      false
    );
    this._endPointService.registerEndPoint(
      'activity_comment',
      `${this._apiLearnPathsBase}${this._LearnPathActivitySaveComment}`,
      false
    );
    this._endPointService.registerEndPoint(
      'activity_saveadmin',
      `${this._apiLearnPathsBase}${this._LearnPathActivitySaveAdmin}`,
      false
    );

    this._endPointService.registerEndPoint(
      'send_reminder',
      `${this._apiLearnPathsBase}${this._learnpathReminder}`,
      false
    );

    this._endPointService.registerEndPoint(
      'activity_complete',
      `${this._apiLearnPathsBase}${this._LearnPathActivityComplete}`,
      false
    );
    this._endPointService.registerEndPoint<string>(
      'activity_content_url',
      `${this._apiLearnPathsBase}${this._LearnPathActivityContentUrl}`,
      false
    );
    this._endPointService.registerEndPoint<LearningPathStudentActivityAttachment>(
      'activity_attachment_getall',
      `${this._apiLearnPathsBase}${this._ActivityAttachments}`,
      true
    );
    this._endPointService.registerEndPoint<StudentActivityStudent[]>(
      'activity_students_get',
      `${this._apiLearnPathsBase}${this._ActivityStudents}`,
      true
    );
    this._endPointService.registerEndPoint<Course>(
      'course_by_learningpath',
      '/api/lms/training/bylearningpath',
      true
    );
    this._endPointService.registerEndPoint<number[]>(
      'activity_content_scores',
      `${this._apiLearnPathsBase}${this._activityContentScores}`,
      true
    );
    this._endPointService.registerEndPoint<boolean>(
      'can_delete_existing_activity',
      `${this._apiLearnPathsBase}${this._canDeleteExistingActivity}`,
      true
    );
    this._endPointService.registerEndPoint<void>(
      'delete_existing_activity',
      `${this._apiLearnPathsBase}${this._deleteExistingActivity}`,
      true
    );

    this._endPointService.registerEndPoint<string>(
      'activity_content_description',
      `${this._apiLearnPathsBase}${this._activityContentDescription}`,
      true
    );

    this._endPointService.registerEndPoint<string>(
      'lpcansendremindermails',
      `${this._apiLearnPathsBase}cansendremindermails`,
      true
    );




    // this._endPointService.registerEndPoint<string>(
    //   'activities_detailed',
    //   `${this._apiLearnPathsBase}activity/details`,
    //   true
    // );
  }
  //#endregion

  public canDeleteExistingLearningPathActivity(
    activityId: number,
    fetch?: boolean
  ): Observable<boolean> {
    return this._endPointService.call<boolean>(
      'can_delete_existing_activity',
      activityId,
      fetch
    );
  }

  public deleteExistingLearningPathActivity(
    activityId: number,
    fetch?: boolean
  ): Observable<void> {
    return this._endPointService.call<void>(
      'delete_existing_activity',
      activityId,
      fetch
    );
  }

  public getListData(fetch?: boolean): Observable<LearningPathOverview[]> {
    let delegateId: number = null;

    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this._authService.getActiveDelegation();

      delegateId = activeDelegation?.studentId ?? null;
    }

    return this._endPointService.call<LearningPathOverview[]>(
      'learnpath_overview',
      delegateId,
      fetch
    );
  }

  public getLP(lpid: number, fetch?: boolean): Observable<Learnpath> {
    return this._endPointService.call<Learnpath>('learnpath_get', lpid, fetch);
  }

  public deleteLearningPath(learningPathId: number): Observable<void> {
    return this._endPointService.call<void>(
      'learnpath_delete',
      learningPathId,
      true
    );
  }

  public getActivityContent(
    typeId: number,
    pagingState?: PagingState,
    fetch?: boolean
  ): Observable<ActivityContent[]> {
    const data = { typeId, pagingState };
    return this._endPointService.call<ActivityContent[]>(
      'activity_content_get',
      data,
      fetch
    );
  }

  public getActivityContentCount(
    typeId: number,
    searchString: string,
    fetch?: boolean
  ): Observable<number> {
    const data = { id: typeId, searchString };
    return this._endPointService.call<number>(
      'activity_content_count',
      data,
      fetch
    );
  }

  public getActivities(
    lpid: number,
    fetch?: boolean
  ): Observable<LearnpathActivity[]> {
    return this._endPointService.call<LearnpathActivity[]>(
      'activities_get',
      lpid,
      fetch
    );
  }

  public getActivitiesDetailed(lpid: number): Observable<LearnpathActivityDetailed[]> {
    return this.http.post<LearnpathActivityDetailed[]>(`${this._apiLearnPathsBase}activity/details`, lpid);
  }

  public addActivity(
    activity: LearnpathActivity
  ): Observable<LearnpathActivity> {
    return this._endPointService.call<LearnpathActivity>(
      'activity_add',
      activity,
      true
    );
  }

  public saveActivities(
    activityList: LearnpathActivity[]
  ): Observable<LearnpathActivity[]> {
    return this._endPointService.call<LearnpathActivity[]>(
      'activities_save',
      activityList,
      true
    );
  }

  public saveActivity(
    activity: LearnpathActivity
  ): Observable<LearnpathActivity> {
    return this._endPointService.call<LearnpathActivity>(
      'activity_save',
      activity,
      true
    );
  }

  public deleteActivity(activityId): Observable<void> {
    return this._endPointService.call<void>(
      'activity_delete',
      activityId,
      true
    );
  }

  public getActivityTypes(
    fetch?: boolean
  ): Observable<LearnpathActivityType[]> {
    return this._endPointService.call<LearnpathActivityType[]>(
      'activities_type_get',
      null,
      fetch
    );
  }

  public getStudentActivities(
    learningPathId: number,
    fetch?: boolean
  ): Observable<LearningPathStudentActivityGeneral[]> {
    let delegatedMgrId: number = null;
    let isDelegate: boolean = false;

    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this._authService.getActiveDelegation();

      delegatedMgrId = activeDelegation?.studentId ?? null;
      isDelegate = true;
    }
    const data = { learningPathId, isDelegate, delegatedMgrId };
    return this._endPointService.call<LearningPathStudentActivityGeneral[]>(
      'student_activities',
      data,
      fetch
    );
  }

  public saveLearnPath(learningPath: Learnpath): Observable<Learnpath> {
    return this._endPointService.call<Learnpath>(
      'learnpath_save',
      learningPath,
      true
    );
  }

  public getUnsubscribedEmployees(
    lpid: number,
    pagingState?: PagingState,
    fetch?: boolean
  ): Observable<Student[]> {
    let delegatedMgrId: number = null;
    let isDelegate: boolean = false;

    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this._authService.getActiveDelegation();

      delegatedMgrId = activeDelegation?.studentId ?? null;
      isDelegate = true;
    }
    const data = {
      learningPathId: lpid,
      pagingState,
      isDelegate,
      delegatedMgrId,
    };
    return this._endPointService.call<Student[]>(
      'unsubscribed_get',
      data,
      fetch
    );
  }

  public getSubscribedEmployees(
    learningPathId: number,
    pagingState?: PagingState,
    fetch?: boolean
  ): Observable<LearningPathStudentGeneral[]> {
    let delegatedMgrId: number = null;
    let isDelegate: boolean = false;

    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this._authService.getActiveDelegation();

      delegatedMgrId = activeDelegation?.studentId ?? null;
      isDelegate = true;
    }

    const data = {
      learningPathId,
      pagingState,
      isDelegate,
      delegatedMgrId,
    };
    // return this._endPointService.call<LearningPathStudentGeneral[]>(
    //   'subscribed_get',
    //   data,
    //   fetch
    // );
    return this.http.post<LearningPathStudentGeneral[]>(`${this._apiLearnPathsBase}${this._LearnPathSubscribed}`, data);
  }

  public getStudentOverview(
    lpid: number,
    fetch?: boolean
  ): Observable<StudentOverview> {
    return this._endPointService.call<StudentOverview>(
      'studentoverview_get',
      { value: lpid },
      fetch
    );
  }

  public getStudentDashboard(
    lpid: number,
    fetch?: boolean
  ): Observable<LearningPathStudentDashboard> {
    return this._endPointService.call<LearningPathStudentDashboard>(
      'studentdashboard_get',
      { value: lpid },
      fetch
    );
  }

  public getStudentDetail(
    studentId: number,
    fetch?: boolean
  ): Observable<LearningPathStudentDetail> {
    return this._endPointService.call<LearningPathStudentDetail>(
      'studentdetail_get',
      { value: studentId },
      fetch
    );
  }

  public inviteEmployee(
    lpid: number,
    student: Student
  ): Observable<LearningPathStudentGeneral> {
    const pdata = { learningPathId: lpid, studentId: student.studentId };
    return this._endPointService.call<LearningPathStudentGeneral>(
      'employee_invite',
      pdata,
      true
    );
  }

  public subscribeEmployee(
    subscription: LearningPathStudentSubscription
  ): Observable<LearningPathStudentGeneral> {
    return this._endPointService.call<LearningPathStudentGeneral>(
      'employee_subscribe',
      subscription,
      true
    );
  }

  public subscribeSelf(learningPathId: number): Observable<LearningPathStudentGeneral> {
    return this._endPointService.call<LearningPathStudentGeneral>(
      'employee_subscribeemp',
      { learningPathId }
    );
  }

  public completeEmployeeLearningpath(learningPathStudentId: number): Observable<void> {
    return this._endPointService.call<void>(
      'employee_complete',
      { value: learningPathStudentId },
      true
    );
  }

  public resumeEmployeeLearningpath(learningPathStudentId: number): Observable<void> {
    return this._endPointService.call<void>(
      'employee_resume',
      { value: learningPathStudentId },
      true
    );
  }

  public restartEmployeeLearningpath(learningPathStudentId: number): Observable<void> {
    return this._endPointService.call<void>(
      'employee_restart',
      { value: learningPathStudentId },
      true
    );
  }

  public unsubscribeEmployee(studentId: number): Observable<void> {
    return this._endPointService.call<void>(
      'employee_unsubscribe',
      { value: studentId },
      true
    );
  }

  public getLongestNotStartedStudent(
    learningPathId: number,
    fetch?: boolean
  ): Observable<LearningPathStudent> {
    return this._endPointService.call<LearningPathStudent>(
      'student_longest_notstarted_get',
      { value: learningPathId },
      fetch
    );
  }

  public getActivitiesWithHelp(
    lpid: number,
    fetch?: boolean
  ): Observable<StudentActivityWithHelpers> {
    // CurrentStudent
    return this._endPointService.call<StudentActivityWithHelpers>(
      'activities_with_help_get',
      lpid,
      fetch
    );
  }

  public getMyActivities(
    lpid: number,
    fetch?: boolean
  ): Observable<LearningPathStudentActivityGeneral[]> {
    // CurrentStudent
    return this._endPointService.call<LearningPathStudentActivityGeneral[]>(
      'myactivities_get',
      lpid,
      fetch
    );
  }

  public getMyLearnpaths(fetch?: boolean): Observable<LearningPathStudent[]> {
    // CurrentStudent
    return this._endPointService.call<LearningPathStudent[]>(
      'mylearnpaths_get',
      null,
      fetch
    );
  }

  public getMyLearnpath(
    lpid: number,
    fetch?: boolean
  ): Observable<LearningPathStudent> {
    // CurrentStudent
    return this._endPointService.call<LearningPathStudent>(
      'mylearnpath_get',
      lpid,
      fetch
    );
  }

  public acceptLearnpath(learningPathStudentId: number, fetch?: boolean) {
    return this._endPointService.call<LearningPathStudent>(
      'mylearnpath_accept',
      learningPathStudentId,
      fetch
    );
  }

  public getStudentActivityStatusses(
    fetch?: boolean
  ): Observable<LearningPathStudentActivityStatus[]> {
    return this._endPointService.call<Array<LearningPathStudentActivityStatus>>(
      'activityStatusses_get',
      null,
      fetch
    );
  }

  public changeStudentActivityStatus(
    activityId: number,
    statusId: number,
    message: string
  ): Observable<LearningPathStudentActivityGeneral> {
    const data = { activityId, statusId, message };
    return this._endPointService.call<LearningPathStudentActivityGeneral>(
      'activityStatus_change',
      data,
      true
    );
  }

  public getStudentActivityHistory(
    studentActivityId: number,
    fetch?: boolean
  ): Observable<LearningPathStudentActivityStatusLog[]> {
    return this._endPointService.call<LearningPathStudentActivityStatusLog[]>(
      'activity_status_history',
      studentActivityId,
      fetch
    );
  }

  public updateManagerComment(
    studentActivityId: number,
    managerComment: string
  ): Observable<void> {
    const data = {
      studentActivityId,
      managerComment,
    };
    return this._endPointService.call<void>(
      'activity_add_manager_comment',
      data,
      false
    );
  }

  public removeAttachment(attachmentID: number) {
    return this._endPointService.call(
      'activity_delete_attachment',
      attachmentID,
      true
    );
  }

  public checkActivityStatus(learningPathId: number): Observable<boolean> {
    return this._endPointService.call('activity_check', learningPathId, true);
  }

  public startActivity(learningPathStudentActivityId: number) {
    return this._endPointService.call(
      'activity_start',
      learningPathStudentActivityId,
      true
    );
  }

  public saveActivityComment(activity: LearningPathStudentActivityGeneral) {
    return this._endPointService.call('activity_comment', {
      id: activity.studentActivityId,
      comment: activity.studentComment,
    });
  }

  public saveActivityAdmin(studentActivityId: number, managerComment: string, newStatusId: number) {
    return this._endPointService.call('activity_saveadmin', {
      id: studentActivityId,
      comment: managerComment,
      statusId: newStatusId,
    });
  }



  public sendReminder(learningPathStudentId: number) {
    return this._endPointService.call('send_reminder', learningPathStudentId);
  }

  public SetNotMandatoryActivityToCompleted(activity) {
    return this._endPointService.call('activity_complete', {
      id: activity.studentActivityId,
    });
  }

  public getActivityContentLink(studentActivityId: number): Observable<string> {
    return this.http.post('/api/lms/learningpaths/ess/activity/getcontenturl', studentActivityId, { responseType: 'text' });
    // return this._endPointService.call(
    //   'activity_content_url',
    //   studentActivityId,
    //   true
    // );
  }

  public getActivityAttachments(
    studentActivityId: number,
    fetch?: boolean
  ): Observable<LearningPathStudentActivityAttachment> {
    return this._endPointService.call<LearningPathStudentActivityAttachment>(
      'activity_attachment_getall',
      studentActivityId,
      fetch
    );
  }

  public getActivityStudents(
    activityId: number,
    fetch?: boolean
  ): Observable<StudentActivityStudent[]> {
    let delegatedMgrId: number = null;
    let isDelegate: boolean = false;

    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this._authService.getActiveDelegation();

      delegatedMgrId = activeDelegation?.studentId ?? null;
      isDelegate = true;
    }
    const data = { activityId, isDelegate, delegatedMgrId };
    return this._endPointService.call<StudentActivityStudent[]>(
      'activity_students_get',
      data,
      fetch
    );
  }

  public getLearningPathCourse(
    learningPathId: number,
    fetch?: boolean
  ): Observable<Course> {
    return this._endPointService.call<Course>(
      'course_by_learningpath',
      learningPathId,
      fetch
    );
  }

  public getActivityContentScores(
    activityId: number,
    fetch?: boolean
  ): Observable<number[]> {
    let delegatedMgrId: number = null;
    let isDelegate: boolean = false;

    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this._authService.getActiveDelegation();

      delegatedMgrId = activeDelegation?.studentId ?? null;
      isDelegate = true;
    }
    const data = { activityId, isDelegate, delegatedMgrId };
    return this._endPointService.call<number[]>(
      'activity_content_scores',
      data,
      fetch
    );
  }

  public getActivityContentDescription(
    activityTypeId: number,
    contentId: any,
    fetch?: boolean
  ): Observable<string> {
    let delegatedMgrId: number = null;
    let isDelegate: boolean = false;

    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this._authService.getActiveDelegation();

      delegatedMgrId = activeDelegation?.studentId ?? null;
      isDelegate = true;
    }
    const data = { activityTypeId, contentId, isDelegate, delegatedMgrId };
    return this._endPointService.call<{ description: string }>(
      'activity_content_description',
      data,
      fetch
    ).pipe(map(response => response.description));
  }

  public canSendReminderMails(fetch?: boolean): Observable<boolean> {
    return this._endPointService.call<boolean>('lpcansendremindermails', null, fetch);
  }
}
